<template>
  <div>
    <page-header
      title="角色管理"
      :crumbs="['系统设置', '角色管理']"
      :subtitle="`共 ${roles.length} 个角色`"
    >
      <template #actions>
        <button v-if="hasPermission('sys:role:add')" class="btn btn-primary" @click="showAddDialog">
          新增角色
        </button>
      </template>
    </page-header>

    <DataTable
      :columns="columns"
      :data="roles"
      :total="0"
      row-key="id"
      :ops-width="200"
      empty-text="暂无角色"
      @refresh="getRoles"
    >
      <template #toolbar-left>
        <span class="dt-hint">共 {{ roles.length }} 个角色</span>
      </template>

      <template #cell-status="{ row }">
        <span class="tag" :class="row.status === 1 ? 'tag-success' : 'tag-neutral'">
          {{ row.status === 1 ? '正常' : '禁用' }}
        </span>
      </template>

      <template #ops="{ row }">
        <button v-if="hasPermission('sys:role:edit')" class="dt-op" @click="showEditDialog(row)">编辑</button>
        <span v-if="hasPermission('sys:role:edit')" class="dt-sep">·</span>
        <button v-if="hasPermission('sys:role:permission')" class="dt-op" @click="showPermissionDialog(row)">权限配置</button>
        <span v-if="hasPermission('sys:role:permission')" class="dt-sep">·</span>
        <button v-if="hasPermission('sys:role:delete')" class="dt-op is-danger" @click="deleteRole(row.id)">删除</button>
      </template>
    </DataTable>

    <!-- 新增 / 编辑角色 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="520px">
      <el-form :model="form" label-position="top">
        <el-form-item label="角色名称">
          <el-input v-model="form.roleName" placeholder="请输入角色名称" />
        </el-form-item>
        <el-form-item label="角色编码">
          <el-input v-model="form.roleCode" placeholder="请输入角色编码" />
        </el-form-item>
        <el-form-item label="角色描述">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入角色描述" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" active-value="1" inactive-value="0" active-text="正常" inactive-text="禁用" />
        </el-form-item>

        <template v-if="isAdd">
          <div class="section-title">权限配置</div>
          <div class="tree-box">
            <el-tree
              ref="permissionTreeAdd"
              :data="addPermissions"
              show-checkbox
              node-key="id"
              :props="defaultProps"
              :expand-on-click-node="false"
            />
          </div>
        </template>
      </el-form>
      <template #footer>
        <button class="btn" @click="dialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="saveRole">保存</button>
      </template>
    </el-dialog>

    <!-- 权限配置 -->
    <el-dialog v-model="permissionDialogVisible" title="权限配置" width="560px">
      <div class="tree-box">
        <el-tree
          ref="permissionTree"
          :data="permissions"
          show-checkbox
          node-key="id"
          :props="defaultProps"
          :expand-on-click-node="false"
        />
      </div>
      <template #footer>
        <button class="btn" @click="permissionDialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="savePermissions">保存</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { onMounted, reactive, toRefs, ref, nextTick, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import permissionService from '@/services/permissionService'
import roleService from '@/services/roleService'
import store from '@/store'

export default {
  name: 'RoleManagement',
  components: { PageHeader, DataTable },
  setup() {
    const state = reactive({
      roles: [],
      permissions: [],
      addPermissions: [],
      dialogVisible: false,
      permissionDialogVisible: false,
      dialogTitle: '新增角色',
      form: { id: null, roleName: '', roleCode: '', description: '', status: 1 },
      currentRoleId: null,
      defaultProps: { children: 'children', label: 'permissionName', disabled: 'disabled' }
    })

    const permissionTree = ref(null)
    const permissionTreeAdd = ref(null)

    const columns = [
      { prop: 'id', label: 'ID', width: 70 },
      { prop: 'roleName', label: '角色名称', minWidth: 150 },
      { prop: 'roleCode', label: '角色编码', minWidth: 150 },
      { prop: 'description', label: '角色描述', minWidth: 240 },
      { prop: 'status', label: '状态', width: 90 }
    ]

    const isAdd = computed(() => state.dialogTitle === '新增角色')

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

    const hasPermission = (code) => permissionCodes.value.includes(code)

    const getRoles = async () => {
      try {
        const response = await roleService.getRolesByTenantId(store.state.tenantId)
        state.roles = response?.data || []
      } catch {
        ElMessage.error('获取角色列表失败')
      }
    }

    const buildPermissionTree = (list, parentId = 0) =>
      list
        .filter(item => item.parentId === parentId)
        .map(item => {
          const children = buildPermissionTree(list, item.id)
          return { ...item, children: children.length > 0 ? children : null }
        })
        .sort((a, b) => (a.sort || 0) - (b.sort || 0))

    const getPermissions = async () => {
      try {
        const flat = await permissionService.getAllPermissionsByTenantId(store.state.tenantId)
        state.permissions = buildPermissionTree(flat?.data || [])
      } catch {
        ElMessage.error('获取权限列表失败')
      }
    }

    const loadAdminPermissionOptions = async () => {
      try {
        const roleResp = await roleService.getRolesByTenantId(store.state.tenantId)
        const roles = roleResp?.data || []
        const admin = roles.find(r =>
          String(r.roleName || '').toLowerCase() === 'admin' ||
          String(r.roleCode || '').toLowerCase().startsWith('admin')
        )
        let flat = null
        if (admin && admin.id != null) {
          const byRole = await permissionService.getPermissionsByRoleId(admin.id)
          flat = byRole?.data || []
        }
        if (!flat || flat.length === 0) {
          const byTenant = await permissionService.getAllPermissionsByTenantId(store.state.tenantId)
          flat = byTenant?.data || []
        }
        state.addPermissions = buildPermissionTree(flat)
        await nextTick()
        permissionTreeAdd.value?.setCheckedKeys([])
      } catch {
        ElMessage.error('加载管理员权限失败')
      }
    }

    const showAddDialog = () => {
      state.dialogTitle = '新增角色'
      state.form = { id: null, roleName: '', roleCode: '', description: '', status: 1 }
      state.dialogVisible = true
      loadAdminPermissionOptions()
    }

    const showEditDialog = (role) => {
      state.dialogTitle = '编辑角色'
      state.form = { ...role }
      state.dialogVisible = true
    }

    const showPermissionDialog = async (role) => {
      state.currentRoleId = role.id
      state.permissionDialogVisible = true
      try {
        await getPermissions()
        const checked = await permissionService.getPermissionsByRoleId(role.id)
        const checkedIds = (checked?.data || []).map(p => p.id)
        await nextTick()
        permissionTree.value?.setCheckedKeys(checkedIds)
      } catch (error) {
        ElMessage.error('获取角色权限失败' + (error?.message || ''))
      }
    }

    const saveRole = async () => {
      try {
        if (state.form.id) {
          await roleService.update(state.form.id, state.form)
          ElMessage.success('更新成功')
        } else {
          const res = await roleService.create(state.form)
          const newRoleId = res?.data
          if (permissionTreeAdd.value && (typeof newRoleId === 'number' || typeof newRoleId === 'string')) {
            const ids = (permissionTreeAdd.value.getCheckedKeys(true) || [])
              .map(x => Number(x)).filter(n => !Number.isNaN(n))
            if (ids.length > 0) {
              const resp = await permissionService.assignPermissions(Number(newRoleId), ids)
              if (resp?.code !== 200) ElMessage.error('权限配置失败')
            }
          }
          ElMessage.success('新增成功')
        }
        state.dialogVisible = false
        getRoles()
      } catch (error) {
        ElMessage.error(error?.message || '操作失败')
      }
    }

    const savePermissions = async () => {
      if (!permissionTree.value) return
      const checkedIds = permissionTree.value.getCheckedKeys(true)
      try {
        const response = await permissionService.assignPermissions(state.currentRoleId, checkedIds)
        if (response?.code === 200) {
          ElMessage.success('权限配置成功')
        } else {
          ElMessage.error('权限更新失败')
        }
        state.permissionDialogVisible = false
      } catch (error) {
        ElMessage.error(error?.message || '配置失败')
      }
    }

    const deleteRole = async (id) => {
      try {
        await ElMessageBox.confirm('确定要删除该角色吗？', '提示', {
          confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
        })
      } catch {
        return
      }
      try {
        await roleService.delete(id)
        ElMessage.success('删除成功')
        getRoles()
      } catch (error) {
        ElMessage.error(error?.message || '删除失败')
      }
    }

    onMounted(getRoles)

    return {
      ...toRefs(state), columns, isAdd,
      showAddDialog, showEditDialog, showPermissionDialog,
      saveRole, savePermissions, deleteRole, getRoles, hasPermission,
      permissionTree, permissionTreeAdd
    }
  }
}
</script>

<style scoped>
.tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: var(--radius-tag);
  white-space: nowrap;
}

.tag-success { background: var(--success-bg); color: var(--success-text); }
.tag-neutral { background: var(--bg-page);    color: var(--text-secondary); }

.dt-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.section-title {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  padding-top: 4px;
  border-top: 1px solid var(--border);
}

.tree-box {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  padding: 8px;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
